import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import axios from 'axios';
import { Globe, Target, TrendingUp, Clock, CalendarDays, Users, Bot, Plus, ChevronRight, RefreshCw, ArrowLeft, Loader2, FileText, X, Briefcase, Zap, MapPin, Award, Landmark, Music, Plane, ShoppingBag, Cpu, UserPlus, Star, Search } from 'lucide-react';
const fmtUK = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };


const API = process.env.REACT_APP_BACKEND_URL;
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const FILTER_CATS = [
  { id: 'all', label: 'All', icon: Globe, color: '#B8860B' },
  { id: 'trade', label: 'Trade', icon: Briefcase, color: '#2563EB' },
  { id: 'tourism', label: 'Tourism', icon: Plane, color: '#059669' },
  { id: 'entertainment', label: 'Entertainment', icon: Music, color: '#7C3AED' },
  { id: 'ai_tech', label: 'AI & Tech', icon: Cpu, color: '#0891B2' },
  { id: 'government', label: 'Government', icon: Landmark, color: '#D97706' },
  { id: 'grant', label: 'Grants', icon: Award, color: '#DB2777' },
  { id: 'sports_event', label: 'Sport', icon: Target, color: '#DC2626' },
];

const scoreColor = (s) => s >= 90 ? '#DC2626' : s >= 75 ? '#D97706' : s >= 50 ? '#B8860B' : s >= 25 ? '#16A34A' : '#8B7355';
const scoreBg = (s) => s >= 90 ? '#FEF2F2' : s >= 75 ? '#FFFBEB' : s >= 50 ? '#FEF9E7' : '#F0FDF4';

const Navigator = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [countries, setCountries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryDetail, setCountryDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [generatingBriefing, setGeneratingBriefing] = useState(false);
  const [showBriefing, setShowBriefing] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('opportunities');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/navigator/dashboard`, { withCredentials: true }),
      axios.get(`${API}/api/navigator/countries`, { withCredentials: true }),
    ]).then(([d, c]) => {
      setDashboard(d.data);
      setCountries(c.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const openCountry = async (code) => {
    setSelectedCountry(code);
    try {
      const res = await axios.get(`${API}/api/navigator/countries/${code}`, { withCredentials: true });
      setCountryDetail(res.data);
    } catch (e) { console.error(e); }
  };

  const generateBriefing = async () => {
    setGeneratingBriefing(true);
    try {
      const res = await axios.post(`${API}/api/navigator/generate-weekly-briefing`, {}, { withCredentials: true });
      setBriefing(res.data.briefing);
      setShowBriefing(true);
    } catch (e) { console.error(e); alert('Briefing generation failed. Please try again.'); }
    setGeneratingBriefing(false);
  };

  const globalScore = dashboard?.global_score || 0;
  const totalPipeline = dashboard?.pipeline?.total || 0;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
      <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#D4AF37' }} />
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FAF9F7' }} data-testid="navigator">
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 flex flex-col bg-white" style={{ borderRight: '1px solid #E5E1DB' }}>
        <div className="p-4 flex items-center gap-3" style={{ borderBottom: '1px solid #E5E1DB' }}>
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: '#D4AF3718' }}>
            <Globe className="w-4 h-4" style={{ color: '#D4AF37' }} />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-wider" style={{ color: '#2C1A0E' }}>MALUTI</p>
            <p className="text-[9px] tracking-[0.2em]" style={{ color: '#B8860B' }}>NAVIGATOR</p>
          </div>
        </div>
        <nav className="flex-1 py-2">
          {[
            { icon: Globe, label: 'Navigator', active: true },
            { icon: Target, label: 'Opportunities', onClick: () => navigate('/bi-dashboard') },
            { icon: CalendarDays, label: 'Calendar', onClick: () => navigate('/planner') },
            { icon: Users, label: 'Leads & CRM', onClick: () => navigate('/navigator/crm') },
            { icon: TrendingUp, label: 'Revenue Pipeline', disabled: true },
            { icon: Award, label: 'Grants & Funding', disabled: true },
            { icon: Landmark, label: 'Trade Missions', disabled: true },
            { icon: FileText, label: 'Reports', onClick: generateBriefing },
            { icon: Bot, label: 'AI Assistant', disabled: true },
          ].map((item) => (
            <button key={item.label} onClick={item.onClick || (() => {})} disabled={item.disabled}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-xs transition-all hover:bg-amber-50/50"
              style={{ backgroundColor: item.active ? '#FAF7F2' : 'transparent', color: item.active ? '#B8860B' : item.disabled ? '#CBD5E1' : '#6B5B4E', borderLeft: item.active ? '2px solid #D4AF37' : '2px solid transparent' }}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
              <item.icon className="w-4 h-4" />
              {item.label}
              {item.disabled && <span className="ml-auto text-[8px] px-1 py-px rounded" style={{ backgroundColor: '#F5F1EB', color: '#B8860B' }}>Soon</span>}
            </button>
          ))}
        </nav>
        <div className="p-4" style={{ borderTop: '1px solid #E5E1DB' }}>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-xs hover:opacity-70" style={{ color: '#8B7355' }}>
            <ArrowLeft className="w-3 h-3" /> Ops Suite
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white flex items-center justify-between px-6 py-3" style={{ borderBottom: '1px solid #E5E1DB' }}>
          <div>
            <h1 className="text-lg font-light tracking-wide" style={{ color: '#2C1A0E' }}>MALUTI NAVIGATOR</h1>
            <p className="text-[10px] tracking-[0.15em]" style={{ color: '#8B7355' }}>Global Opportunities. Smarter Decisions. Bigger Impact.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs" style={{ color: '#6B5B4E' }}>Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, Naledi</p>
              <p className="text-[10px] font-mono" style={{ color: '#8B7355' }}>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div className="px-4 py-2 rounded-sm text-center bg-white" style={{ border: '1px solid #E5E1DB' }}>
              <p className="text-[9px] uppercase tracking-wider" style={{ color: '#8B7355' }}>Global Score</p>
              <p className="text-2xl font-light" style={{ color: scoreColor(globalScore) }}>{globalScore}<span className="text-sm" style={{ color: '#8B7355' }}>/100</span></p>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="flex">
            {/* Map + Content */}
            <div className="flex-1 p-5 space-y-4">
              {/* Map */}
              <div className="bg-white rounded-sm overflow-hidden" style={{ border: '1px solid #E5E1DB' }}>
                <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                  <div className="flex items-center gap-2">
                    <Globe className="w-3.5 h-3.5" style={{ color: '#B8860B' }} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B8860B' }}>Global Opportunity Map</span>
                  </div>
                  <span className="text-[10px]" style={{ color: '#8B7355' }}>{dashboard?.total_opportunities || 0} active opportunities</span>
                </div>
                <div style={{ height: 320, backgroundColor: '#E8DFD0' }}>
                  <ComposableMap projectionConfig={{ rotate: [-10, 0, 0], scale: 145 }} style={{ width: '100%', height: '100%' }}>
                    <ZoomableGroup center={[10, 15]} zoom={1.2}>
                      <Geographies geography={GEO_URL}>
                        {({ geographies }) => geographies.map(geo => (
                          <Geography key={geo.rpiproperties?.NAME || geo.id} geography={geo}
                            style={{
                              default: { fill: '#C4B89A', stroke: '#A89878', strokeWidth: 0.5 },
                              hover: { fill: '#B8A680', stroke: '#D4AF37', strokeWidth: 0.8 },
                              pressed: { fill: '#D4AF3740' },
                            }}
                          />
                        ))}
                      </Geographies>
                      {countries.map(c => {
                        const avg = Math.round(Object.values(c.scores || {}).reduce((a, b) => a + b, 0) / Math.max(Object.values(c.scores || {}).length, 1));
                        return (
                          <Marker key={c.code} coordinates={[c.lng, c.lat]}>
                            <g onClick={() => openCountry(c.code)} style={{ cursor: 'pointer' }}>
                              <circle r={7} fill={scoreColor(avg)} fillOpacity={0.9} stroke="#FFF" strokeWidth={1.5} />
                              <text y={-12} textAnchor="middle" style={{ fontSize: 8, fill: '#2C1A0E', fontWeight: 600 }}>{c.flag} {c.code}</text>
                              <text y={3} textAnchor="middle" style={{ fontSize: 7, fill: '#FFF', fontWeight: 700 }}>{avg}</text>
                            </g>
                          </Marker>
                        );
                      })}
                    </ZoomableGroup>
                  </ComposableMap>
                </div>
                <div className="px-4 py-2 flex items-center gap-2 overflow-x-auto" style={{ borderTop: '1px solid #E5E1DB', backgroundColor: '#FDFCFB' }}>
                  <span className="text-[9px] mr-1" style={{ color: '#8B7355' }}>Filter:</span>
                  {FILTER_CATS.map(f => (
                    <button key={f.id} onClick={() => setFilter(f.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-medium whitespace-nowrap transition-all"
                      style={{ backgroundColor: filter === f.id ? f.color + '15' : '#FFF', color: filter === f.id ? f.color : '#8B7355', border: `1px solid ${filter === f.id ? f.color + '40' : '#E5E1DB'}` }}>
                      <f.icon className="w-3 h-3" /> {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Country Card + Revenue */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-sm p-4" style={{ border: '1px solid #E5E1DB' }}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B8860B' }}>
                      {countryDetail ? `${countryDetail.flag} ${countryDetail.name?.toUpperCase()}` : '🇬🇧 UNITED KINGDOM'}
                    </span>
                    <span className="text-lg font-light" style={{ color: '#D4AF37' }}>
                      {countryDetail ? Math.round(Object.values(countryDetail.scores || {}).reduce((a, b) => a + b, 0) / Math.max(Object.values(countryDetail.scores || {}).length, 1)) : 96}/100
                    </span>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(countryDetail?.scores || { trade: 90, tourism: 95, entertainment: 100, ai: 85, government: 90, funding: 85 }).map(([key, val]) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-[10px] w-24 capitalize" style={{ color: '#8B7355' }}>{key.replace('_', ' ')}</span>
                        <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: '#F5F1EB' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${val}%`, backgroundColor: scoreColor(val) }} />
                        </div>
                        <span className="text-[10px] font-mono w-6 text-right" style={{ color: scoreColor(val) }}>{val}</span>
                      </div>
                    ))}
                  </div>
                  {countryDetail?.opportunities?.length > 0 && (
                    <div className="mt-3 pt-2" style={{ borderTop: '1px solid #F5F1EB' }}>
                      <p className="text-[9px] uppercase tracking-wider mb-2 font-semibold" style={{ color: '#B8860B' }}>Top Opportunities</p>
                      {countryDetail.opportunities.slice(0, 4).map(o => (
                        <p key={o.id} className="text-[10px] py-0.5" style={{ color: '#6B5B4E' }}>
                          <span style={{ color: '#D4AF37' }}>•</span> {o.name}
                          {o.date && <span className="ml-1 font-mono" style={{ color: '#8B7355' }}>({fmtUK(o.date)})</span>}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-sm p-4" style={{ border: '1px solid #E5E1DB' }}>
                  <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B8860B' }}>Revenue Pipeline</span>
                  <p className="text-3xl font-light mt-2" style={{ color: '#2C1A0E' }}>£{totalPipeline >= 1000000 ? (totalPipeline / 1000000).toFixed(1) + 'M' : (totalPipeline / 1000).toFixed(0) + 'K'}</p>
                  <p className="text-[10px] mb-3" style={{ color: '#8B7355' }}>Total Potential Value</p>
                  {dashboard?.pipeline?.by_category && (
                    <div className="space-y-1.5">
                      {Object.entries(dashboard.pipeline.by_category).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([cat, val]) => (
                        <div key={cat} className="flex items-center justify-between py-0.5" style={{ borderBottom: '1px solid #FAF7F2' }}>
                          <span className="text-[10px] capitalize" style={{ color: '#6B5B4E' }}>{cat}</span>
                          <span className="text-[10px] font-mono" style={{ color: '#B8860B' }}>£{val >= 1000 ? (val / 1000).toFixed(0) + 'K' : val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-sm p-3" style={{ border: '1px solid #E5E1DB' }}>
                  <p className="text-[9px] uppercase tracking-wider font-semibold mb-2" style={{ color: '#D97706' }}>Early Signals</p>
                  {['Experience Africa 2027 buyer registration Aug-Sep', 'SAT UK roadshow expected August', 'Black History Month planning Jul-Aug', 'Corporate gifting procurement starts Aug', 'BA Trade Partner Programme expected Sep'].map((s, i) => (
                    <p key={i} className="text-[10px] py-1" style={{ color: '#6B5B4E' }}><span style={{ color: '#D97706' }}>•</span> {s}</p>
                  ))}
                </div>
                <div className="bg-white rounded-sm p-3" style={{ border: '1px solid #E5E1DB' }}>
                  <p className="text-[9px] uppercase tracking-wider font-semibold mb-2" style={{ color: '#2563EB' }}>This Week&apos;s Actions</p>
                  {(dashboard?.actions_this_week || []).slice(0, 5).map((a, i) => (
                    <p key={a.id || i} className="text-[10px] py-1" style={{ color: '#6B5B4E' }}>{i + 1}. {a.name}</p>
                  ))}
                  {(!dashboard?.actions_this_week?.length) && <p className="text-[10px]" style={{ color: '#CBD5E1' }}>No urgent actions</p>}
                </div>
                <div className="bg-white rounded-sm p-3" style={{ border: '1px solid #E5E1DB' }}>
                  <p className="text-[9px] uppercase tracking-wider font-semibold mb-2" style={{ color: '#7C3AED' }}>AI Assistant</p>
                  {['Find tourism organisations at WTM London', 'Show grants closing this month', 'Opportunities generating >£20k?', 'SA suppliers at Manufacturing Indaba'].map((q, i) => (
                    <p key={i} className="text-[10px] py-1 px-2 mb-1 rounded-sm cursor-pointer" style={{ color: '#6B5B4E', backgroundColor: '#FAF7F2' }}>{q} →</p>
                  ))}
                </div>
                <div className="bg-white rounded-sm p-3" style={{ border: '1px solid #E5E1DB' }}>
                  <p className="text-[9px] uppercase tracking-wider font-semibold mb-2" style={{ color: '#B8860B' }}>Quick Add</p>
                  {[
                    { label: 'Add Lead', icon: UserPlus, onClick: () => navigate('/navigator/crm') },
                    { label: 'Add Opportunity', icon: Target, onClick: () => navigate('/bi-dashboard?new=1') },
                    { label: 'Add Task', icon: CalendarDays },
                    { label: 'Add Note', icon: FileText },
                  ].map(item => (
                    <button key={item.label} onClick={item.onClick || (() => {})}
                      className="w-full flex items-center gap-2 px-2 py-1.5 mb-1 text-[10px] rounded-sm transition-all hover:bg-amber-50"
                      style={{ color: '#6B5B4E' }}>
                      <item.icon className="w-3 h-3" style={{ color: '#D4AF37' }} /> {item.label} <ChevronRight className="w-2.5 h-2.5 ml-auto" style={{ color: '#CBD5E1' }} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="w-72 flex-shrink-0 bg-white overflow-y-auto" style={{ borderLeft: '1px solid #E5E1DB' }}>
              <div className="p-4" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#B8860B' }}>Today&apos;s Opportunities</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}>{dashboard?.todays_opportunities?.length || 0}</span>
                </div>
                {dashboard?.todays_opportunities?.length > 0 ? dashboard.todays_opportunities.map(o => (
                  <div key={o.id} className="py-2" style={{ borderBottom: '1px solid #F5F1EB' }}>
                    <p className="text-[11px] font-medium" style={{ color: '#2C1A0E' }}>{o.name}</p>
                    <p className="text-[9px]" style={{ color: o.deadline === dashboard?.date ? '#DC2626' : '#059669' }}>
                      {o.deadline === dashboard?.date ? 'Deadline Today' : 'Opens Today'}
                    </p>
                  </div>
                )) : <p className="text-[10px]" style={{ color: '#CBD5E1' }}>No opportunities opening today</p>}
                <button onClick={() => navigate('/bi-dashboard')} className="text-[10px] mt-2 flex items-center gap-1" style={{ color: '#B8860B' }}>
                  View all opportunities <ChevronRight className="w-3 h-3" />
                </button>
              </div>

              <div className="p-4" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#D97706' }}>Deadlines (30 Days)</span>
                <div className="mt-3 space-y-2">
                  {(dashboard?.deadlines_30_days || []).slice(0, 6).map(d => (
                    <div key={d.id} className="flex items-center justify-between">
                      <div className="min-w-0 flex-1 mr-2">
                        <p className="text-[10px] truncate" style={{ color: '#2C1A0E' }}>{d.name}</p>
                        <p className="text-[9px] font-mono" style={{ color: '#8B7355' }}>{d.deadline}</p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-sm flex-shrink-0"
                        style={{ backgroundColor: scoreBg(100 - d.days_left * 3), color: d.days_left <= 7 ? '#DC2626' : d.days_left <= 14 ? '#D97706' : '#16A34A' }}>
                        {d.days_left}D
                      </span>
                    </div>
                  ))}
                  {!dashboard?.deadlines_30_days?.length && <p className="text-[10px]" style={{ color: '#CBD5E1' }}>No deadlines in next 30 days</p>}
                </div>
              </div>

              <div className="p-4">
                <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#7C3AED' }}>Weekly Briefing</span>
                <button onClick={generateBriefing} disabled={generatingBriefing}
                  className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-sm text-xs font-medium transition-all hover:shadow-sm"
                  style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }}
                  data-testid="generate-briefing">
                  {generatingBriefing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  {generatingBriefing ? 'Generating...' : 'Generate Weekly Report'}
                </button>
                <p className="text-[9px] mt-2 text-center" style={{ color: '#8B7355' }}>12-month look-ahead powered by AI</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Country Panel */}
      {selectedCountry && countryDetail && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ backgroundColor: 'rgba(44,26,14,0.4)' }} onClick={() => { setSelectedCountry(null); setCountryDetail(null); }}>
          <div className="w-full max-w-md h-full bg-white overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="country-panel">
            <div className="px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderBottom: '1px solid #E5E1DB' }}>
              <div>
                <p className="text-lg font-light" style={{ color: '#2C1A0E' }}>{countryDetail.flag} {countryDetail.name}</p>
                <p className="text-[10px]" style={{ color: '#8B7355' }}>
                  Score: {Math.round(Object.values(countryDetail.scores || {}).reduce((a, b) => a + b, 0) / Math.max(Object.values(countryDetail.scores || {}).length, 1))}/100 · {countryDetail.active_opportunities} opportunities · £{((countryDetail.pipeline_value || 0) / 1000).toFixed(0)}K pipeline
                </p>
              </div>
              <button onClick={() => { setSelectedCountry(null); setCountryDetail(null); }}><X className="w-5 h-5" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                {Object.entries(countryDetail.scores || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="text-xs w-28 capitalize" style={{ color: '#8B7355' }}>{key.replace('_', ' ')}</span>
                    <div className="flex-1 h-2 rounded-full" style={{ backgroundColor: '#F5F1EB' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${val}%`, backgroundColor: scoreColor(val) }} />
                    </div>
                    <span className="text-xs font-mono w-8 text-right" style={{ color: scoreColor(val) }}>{val}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: '#B8860B' }}>Opportunities ({countryDetail.opportunities?.length || 0})</p>
                {(countryDetail.opportunities || []).map(o => (
                  <div key={o.id} className="py-2" style={{ borderBottom: '1px solid #F5F1EB' }}>
                    <p className="text-xs font-medium" style={{ color: '#2C1A0E' }}>{o.name}</p>
                    <p className="text-[10px]" style={{ color: '#8B7355' }}>{fmtUK(o.date)} · {o.organisation} · £{(o.estimated_revenue || 0).toLocaleString()}</p>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(o.relevant_businesses || []).map(b => (
                        <span key={b} className="text-[8px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: '#FAF7F2', color: '#B8860B', border: '1px solid #E5E1DB' }}>{b}</span>
                      ))}
                    </div>
                  </div>
                ))}
                {!countryDetail.opportunities?.length && <p className="text-[10px]" style={{ color: '#CBD5E1' }}>No opportunities added yet for this country</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Briefing Modal */}
      {showBriefing && briefing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,26,14,0.5)' }}>
          <div className="w-full max-w-3xl max-h-[85vh] bg-white overflow-y-auto rounded-sm shadow-2xl" style={{ border: '1px solid #E5E1DB' }}>
            <div className="px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderBottom: '1px solid #E5E1DB' }}>
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>Weekly Intelligence Briefing</p>
                  <p className="text-[10px]" style={{ color: '#8B7355' }}>Generated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
              <button onClick={() => setShowBriefing(false)}><X className="w-5 h-5" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="p-6 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#4A3A2E' }}>{briefing}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navigator;
